import { AxiosResponse } from 'axios'
import { ComponentLikeType, ComponentTypesV2 } from 'opensolar-common'
import { useEffect, useState } from 'react'
import { http } from '../services/http'
import { PARTNER_TYPE_MANUFACTURER, PARTNER_TYPE_HARDWARE_SUPPLIER, PartnerType } from 'opensolar-common'
import { isValidImageUrl } from './utils'

interface UseLoadFeaturedProductProps {
  partnerType: PartnerType
  manufacturers?: string[]
  hardwareSuppliers?: string[]
  countriesInclude?: string
  componentType?: ComponentTypesV2[]
}

const sortProductsByImageWithWorkingCheck = async (products: ComponentLikeType[]): Promise<ComponentLikeType[]> => {
  const withWorkingImage: ComponentLikeType[] = []
  const withoutOrBrokenImage: ComponentLikeType[] = []
  for (const product of products) {
    const imageUrl = product?.ordering_v2?.find((orderingV2) => orderingV2?.image_url)?.image_url ?? ''
    const hasImageUrl = Boolean(imageUrl)

    if (!hasImageUrl) {
      withoutOrBrokenImage.push(product)
      continue
    }

    const isWorking = await isValidImageUrl(imageUrl)
    if (isWorking) {
      withWorkingImage.push(product)
    } else {
      withoutOrBrokenImage.push(product)
    }
  }

  return [...withWorkingImage, ...withoutOrBrokenImage]
}

// Endpoint documentation here: https://opensolar.slab.com/posts/15964-documentation-componentsv-2-endpoint-bglvgyxw
const fetchProducts = async (
  manufacturersIds: number[],
  hardwareSuppliers: string[],
  countryCodeISO2: string,
  componentType: ComponentTypesV2[],
): Promise<AxiosResponse> => {
  const params = new URLSearchParams({
    country_iso2: countryCodeISO2,
    types: JSON.stringify(componentType),
    fieldset: 'list',
    limit: '20',
    page: '1',
    in_stock: 'true',
  })

  if (hardwareSuppliers.length) {
    params.append('distributors', JSON.stringify(hardwareSuppliers))
  } else {
    params.append('manufacturers', JSON.stringify(manufacturersIds))
  }

  return http.os.get({
    org: true,
    url: `componentsv2/?${params.toString()}`,
  })
}

const extractIdsFromHyperlinks = (manufacturers) => {
  if (!Array.isArray(manufacturers)) {
    return []
  }

  const ids: number[] = []
  const regex = /\/(\d+)/

  for (const link of manufacturers) {
    if (!link || typeof link !== 'string') {
      continue
    }
    const match = link.match(regex)
    if (match && match[1]) {
      const id = parseInt(match[1], 10)
      ids.push(id)
    }
  }

  return ids
}

export const useLoadFeaturedProducts = ({
  partnerType = PARTNER_TYPE_MANUFACTURER,
  manufacturers = [],
  hardwareSuppliers = [],
  countriesInclude = '',
  componentType = [],
}: UseLoadFeaturedProductProps) => {
  const [featuredProductData, setFeaturedProductData] = useState<ComponentLikeType[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        let allProducts: ComponentLikeType[] = []

        if (partnerType === PARTNER_TYPE_MANUFACTURER) {
          if (!manufacturers.length) {
            setFeaturedProductData([])
            return
          }

          const manufacturersIds = extractIdsFromHyperlinks(manufacturers)
          const result: AxiosResponse = await fetchProducts(manufacturersIds, [], countriesInclude, componentType)

          if (result.status === 200 && result.data) {
            allProducts = result.data
          } else {
            console.error(result)
            return
          }

        } else if (partnerType === PARTNER_TYPE_HARDWARE_SUPPLIER) {
          if (!hardwareSuppliers.length) {
            setFeaturedProductData([])
            return
          }

          const result: AxiosResponse = await fetchProducts([], hardwareSuppliers, countriesInclude, componentType)
          if (result.status === 200 && result.data) {
            allProducts = result.data
          } else {
            console.error(result)
            return
          }
        }

        const productMap = new Map<string | number, ComponentLikeType>()
        for (const product of allProducts) {
          const key = product.code ?? product.id
          productMap.set(key, product)
        }
        const uniqueProducts = Array.from(productMap.values())
        const sortedData = await sortProductsByImageWithWorkingCheck(uniqueProducts)
        setFeaturedProductData(sortedData.slice(0, 10))

      } catch (err) {
        console.error(err)
        setFeaturedProductData([])
      }
    }

    fetchData()
  }, [partnerType, manufacturers, hardwareSuppliers, countriesInclude, componentType])

  return featuredProductData
}
